import { IComponent, ICustomEventData } from '@modules/src/interfaces/IComponents'
import { ContainersNames } from '../../containers/ContainersNames'
import { AssetType } from '@modules/src/enums/AssetType'
import { Orientation } from '@modules/src/enums/Orientation'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import GameSignals from '@/signals/GameSignals'
import Facade from '@/Facade'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import { DeviceUtils } from '@modules/src/utils/DeviceUtils'
import gsap from 'gsap'
import { isFreeSpins } from '@/managers/ui/BackgroundManager'
import StateSignals from '@stateMachine/StateSignals'
import { GameStatesEnum } from '@/states/GameStatesEnum'

//## Mobile, Landscape check.
const RESPIN_COUNTER_LANDSCAPE_Y = DeviceUtils.isMobileDevice() ? 800 : 333.5

type RespinCounterRuntimeState = {
    hasShownEntryValue: boolean
    isRespinSessionActive: boolean
}

const RUNTIME_STATE_KEY = '__respinCounterRuntimeState'

//## featureIntroContinue carries no payload, so the kind of the intro being dismissed is
//## tracked from the matching featureIntroRequested.
let lastFeatureIntroKind: string = ''

const showCounter = (element: any) => {
    element.alpha = 1
    element.visible = true
}

const hideCounter = (element: any) => {
    element.alpha = 0
    element.visible = false
}

const shouldShowRespinUi = (): boolean => {
    const spinData: any = Facade.model?.spinData || {}
    const spinType = String(spinData?.spin_type || '').toLowerCase()
    const isHoldAndWinActive = Boolean(Facade.model?.spinState?.isHoldAndWinActive)

    const hasRespinContext =
        isHoldAndWinActive ||
        spinType === 'respin'

    return hasRespinContext && (isHoldAndWinActive || !Boolean(Facade.model?.roundState?.isFreeSpins || isFreeSpins()))
}

const clampPoints = (value: number): number => {
    return Math.max(0, Math.min(3, Math.floor(Number(value) || 0)))
}

const getRuntimeState = (element: any): RespinCounterRuntimeState => {
    if (!element[RUNTIME_STATE_KEY]) {
        element[RUNTIME_STATE_KEY] = {
            hasShownEntryValue: false,
            isRespinSessionActive: false,
        } as RespinCounterRuntimeState
    }

    return element[RUNTIME_STATE_KEY] as RespinCounterRuntimeState
}

const getSpinDataRespinState = () => {
    const spinData: any = Facade.model?.spinData || {}
    const spinType = String(spinData?.spin_type || '').toLowerCase()
    const nextRoundType = String(spinData?.next_round_type || '').toLowerCase()
    const respinsLeftRaw = Number(spinData?.respins_left ?? 0)
    const respinsTotalRaw = Number(spinData?.respins_total ?? 0)
    const respinsLeft = Number.isFinite(respinsLeftRaw) ? Math.max(0, respinsLeftRaw) : 0
    const respinsTotal = Number.isFinite(respinsTotalRaw) ? Math.max(0, respinsTotalRaw) : respinsLeft

    return { spinType, nextRoundType, respinsLeft, respinsTotal }
}

const hasRespinModelContext = (): boolean => {
    const { spinType, nextRoundType, respinsLeft } = getSpinDataRespinState()
    return spinType === 'respin' || ((nextRoundType === 'respins' || nextRoundType === 'respin') && respinsLeft > 0)
}

const syncCounterPoints = (container: any, pointsToShow: number) => {
    const clamped = clampPoints(pointsToShow)

    for (let index = 1; index <= 3; index++) {
        const point = container?.getChildByName?.(`RESPIN_COUNTER_POINT_${index}`)
        const glow = container?.getChildByName?.(`RESPIN_COUNTER_POINT_GLOW_${index}`)
        const active = index <= clamped

        if (point) point.visible = active
        if (glow) glow.visible = active
    }
}

const showCounterWithPoints = (element: any, pointsToShow: number): void => {
    syncCounterPoints(element, pointsToShow)
    showCounter(element)
}

const hideAndResetCounter = (element: any): void => {
    const state = getRuntimeState(element)
    state.hasShownEntryValue = false
    state.isRespinSessionActive = false
    hideCounter(element)
}

const syncCounterForRestore = (element: any): void => {
    window.setTimeout(() => {
        if (!hasRespinModelContext()) {
            hideAndResetCounter(element)
            return
        }

        const state = getRuntimeState(element)
        state.hasShownEntryValue = true
        state.isRespinSessionActive = true

        const { respinsLeft } = getSpinDataRespinState()
        showCounterWithPoints(element, respinsLeft)
    }, 0)
}

const syncCounterForFeatureEntry = (element: any): void => {
    //## Only the Hold&Win intro opens a respin session. A free spins intro/retrigger dismissed
    //## on a spin that also triggered Hold&Win would otherwise show the counter one tick later,
    //## while the free game ladder is still on screen.
    if (lastFeatureIntroKind !== 'holdandwin') {
        return
    }

    const { respinsLeft, respinsTotal } = getSpinDataRespinState()

    window.setTimeout(() => {
        if (!shouldShowRespinUi()) {
            return
        }

        const state = getRuntimeState(element)
        state.isRespinSessionActive = true

        if (state.hasShownEntryValue) {
            showCounter(element)
            return
        }

        state.hasShownEntryValue = true
        showCounterWithPoints(element, Math.max(respinsTotal, respinsLeft))
    }, 0)
}

const syncCounterForBoardSpinStart = (element: any, respinsLeftRaw?: unknown): void => {
    window.setTimeout(() => {
        const state = getRuntimeState(element)
        if (!state.isRespinSessionActive) {
            return
        }

        const respinsLeft = Number(respinsLeftRaw)
        if (Number.isFinite(respinsLeft)) showCounterWithPoints(element, respinsLeft)
    }, 0)
}

const syncCounterAfterBoardPresentation = (element: any, respinsLeftRaw?: unknown): void => {
    window.setTimeout(() => {
        const state = getRuntimeState(element)
        if (!state.isRespinSessionActive) {
            return
        }

        const respinsLeft = Number(respinsLeftRaw)
        if (Number.isFinite(respinsLeft)) showCounterWithPoints(element, respinsLeft)
    }, 0)
}

const syncCounterOnResizeOrIntroContinue = (element: any): void => {
    window.setTimeout(() => {
        const state = getRuntimeState(element)
        if (!state.isRespinSessionActive) {
            return
        }

        showCounter(element)
    }, 0)
}

const syncCounterByState = (element: any, stateId: string): void => {
    if (stateId === GameStatesEnum.HOLD_AND_WIN_SUMMARY_STATE) {
        showCounterWithPoints(element, 0)
        return
    }

    if (
        stateId === GameStatesEnum.HOLD_AND_WIN_ACTIVATE_STATE ||
        stateId === GameStatesEnum.HOLD_AND_WIN_INTRO_STATE ||
        stateId === GameStatesEnum.FREE_SPINS_SUMMARY_STATE
    ) {
        hideAndResetCounter(element)
        return
    }

    if (Boolean(Facade.model?.spinState?.isHoldAndWinActive)) {
        const state = getRuntimeState(element)
        if (state.isRespinSessionActive) showCounter(element)
        return
    }

    hideAndResetCounter(element)
}

export const RESPIN_COUNTER_COMPONENT: IComponent = {
    name: ContainersNames.RESPIN_COUNTER_CONTAINER,
    type: AssetType.CONTAINER,
    visible: false,
    position: [
        {
            orientation: Orientation.LANDSCAPE,
            x: 1620,
            y: RESPIN_COUNTER_LANDSCAPE_Y,
        },
        {
            orientation: Orientation.PORTRAIT,
            x: 403.5,
            y: 475.5,
        },
    ],
    scale: [
        {
            orientation: Orientation.LANDSCAPE,
            x: 0.7,
            y: 0.7,
        },
        {
            orientation: Orientation.PORTRAIT,
            x: 0.7,
            y: 0.7,
        },
    ],
    events: [
        {
            onGameSignalsTriggered: [(GameSignals as any).featureIntroRequested],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                const element = data.component.componentElement
                lastFeatureIntroKind = String((data.signalData as any)?.kind || '')
                gsap.killTweensOf(element)
                hideAndResetCounter(element)
            },
        },
        {
            onGameSignalsTriggered: [
                GameSignals.previousRoundActive,
            ],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                const element = data.component.componentElement
                gsap.killTweensOf(element)
                syncCounterForRestore(element)
            },
        },
        {
            onGameSignalsTriggered: [(GameSignals as any).holdAndWinBoardSpinStarted],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                const element = data.component.componentElement
                gsap.killTweensOf(element)
                syncCounterForBoardSpinStart(element, data.signalData)
            },
        },
        {
            onGameSignalsTriggered: [(GameSignals as any).holdAndWinRespinsCounterUpdated],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                const element = data.component.componentElement
                gsap.killTweensOf(element)
                syncCounterAfterBoardPresentation(element, data.signalData)
            },
        },
        {
            onGameSignalsTriggered: [ModuleSignals.gameResized],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                const element = data.component.componentElement
                gsap.killTweensOf(element)
                syncCounterOnResizeOrIntroContinue(element)
            },
        },
        {
            onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                const element = data.component.componentElement
                gsap.killTweensOf(element)
                syncCounterForFeatureEntry(element)
            },
        },
        {
            onGameSignalsTriggered: [StateSignals.stateChanged as any],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                const element = data.component.componentElement
                gsap.killTweensOf(element)
                syncCounterByState(element, String((data as any)?.signalData || ''))
            },
        },
        {
            onGameSignalsTriggered: [
                GameSignals.showBaseGameBackground,
                GameSignals.completeSplashScreenState,
                (GameSignals as any).holdAndWinTransitionStart,
                (GameSignals as any).holdAndWinStop,
            ],
            event: ComponentEvent.CUSTOM_NEW,
            callback: function (data: ICustomEventData) {
                const element = data.component.componentElement
                gsap.killTweensOf(element)
                hideAndResetCounter(element)
            },
        },
    ],
    items: [
        {
            name: 'ReSpin Counter Frame',
            type: AssetType.SPRITE,
            src: 'counter-box_re-spin_frame.png',
            anchor: 0.5,
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: -10.5,
                    y: 0,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: -10.5,
                    y: 0,
                },
            ],
        },
        {
            name: 'ReSpin Counter Label',
            type: AssetType.SPRITE,
            src: 'counter-box_re-spin_text.png',
            anchor: 0.5,
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0,
                    y: -56.5,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 0,
                    y: -58,
                },
            ],
        },
        {
            name: 'RESPIN_COUNTER_POINT_GLOW_1',
            type: AssetType.SPRITE,
            src: 'counter-box_re-spin_point_glow.png',
            visible: false,
            anchor: 0.5,
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 1,
                    y: 1,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 1,
                    y: 1,
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 13.5,
                    y: -13,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 13.5,
                    y: -13,
                },
            ],
        },
        {
            name: 'RESPIN_COUNTER_POINT_1',
            type: AssetType.SPRITE,
            src: 'counter-box_re-spin_point.png',
            visible: false,
            anchor: 0.5,
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 80.5,
                    y: 53.5,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 80.5,
                    y: 53.5,
                },
            ],
        },
        {
            name: 'RESPIN_COUNTER_POINT_GLOW_2',
            type: AssetType.SPRITE,
            src: 'counter-box_re-spin_point_glow.png',
            visible: false,
            anchor: 0.5,
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 1,
                    y: 1,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 1,
                    y: 1,
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 107.5,
                    y: -13,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 107.5,
                    y: -13,
                },
            ],
        },
        {
            name: 'RESPIN_COUNTER_POINT_2',
            type: AssetType.SPRITE,
            src: 'counter-box_re-spin_point.png',
            visible: false,
            anchor: 0.5,
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 170.5,
                    y: 53.5,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 170.5,
                    y: 53.5,
                },
            ],
        },
        {
            name: 'RESPIN_COUNTER_POINT_GLOW_3',
            type: AssetType.SPRITE,
            src: 'counter-box_re-spin_point_glow.png',
            visible: false,
            anchor: 0.5,
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 1,
                    y: 1,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 1,
                    y: 1,
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 190,
                    y: -13,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 190,
                    y: -13,
                },
            ],
        },
        {
            name: 'RESPIN_COUNTER_POINT_3',
            type: AssetType.SPRITE,
            src: 'counter-box_re-spin_point.png',
            visible: false,
            anchor: 0.5,
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 255,
                    y: 53.5,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 255,
                    y: 53.5,
                },
            ],
        },
    ],
}
